
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import MessageBox from '@nextrequestco/components/src/components/MessageBox/MessageBox.vue'
import NrEditor from '@nextrequestco/components/src/components/NrEditor/nr-editor.vue'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements/nr-form-label.vue'
import NrButton from '@nextrequestco/components/src/components/NrButton/nr-button.vue'
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon.vue'
import LineItemsTable from './LineItemsTable.vue'
import SurchargesList from './SurchargesList.vue'
import StaffHoursTable from './StaffHoursTable.vue'
import InvoiceAddItemModal from './InvoiceAddItemModal.vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import {
  InvoiceTemplates,
  InvoiceTemplateOption
} from '../types/invoice_templates'
import {
  SelectedLineItemOption,
  SurchargeOption,
  ApiLineItemPayload,
  ApiSurchargeItemPayload
} from '../types/invoice_line_items'
import helpers from '../../../../../utils/helpers.js'
import truncate from 'lodash/truncate'

export default {
  name: 'InvoiceMessageModal',
  components: {
    MessageBox,
    NrSelect,
    NrEditor,
    NrFormLabel,
    NrButton,
    NrIcon,
    LineItemsTable,
    SurchargesList,
    StaffHoursTable,
    InvoiceAddItemModal
  },

  watch: {
    invoiceTemplates(): void {
      this.templates = this.invoiceTemplates.map((item: InvoiceTemplates) => ({
        value: item.id,
        label: item.invoice_template_name,
        description: item.invoice_template_description
      }))
    },

    displayInvoiceBox(newVal, _): void {
      if (newVal) {
        // Calculate invoice costs on open
        this.recalculateAllCosts()
      }
    },

    requestStaffHours(newVal, _): void {
      if (newVal) {
        this.recalculateAllCosts()
      }
    }
  },

  data() {
    return {
      selectedInvoiceTemplate: [],
      message: '',
      templates: []
    }
  },

  computed: {
    ...mapState(['account']),

    ...mapState('request_show', [
      'requestStaffHours',
      'displayCreateInvoiceMsgBox',
      'currentAction',
      'invoiceTemplates',
      'request',
      'invoiceLineItemType',
      'invoiceLineItemForm',
      'invoiceSelectedSurchargeItem',
      'activeAddItemModal',
      'totalSelectedLineItems',
      'totalSelectedSurchargeItems',
      'subtotalInvoiceAmount',
      'totalInvoiceAmount',
      'invoiceItemToEdit'
    ]),

    tooltipText(): string {
      return this.invalidInvoiceAmount
        ? 'The invoice should total a positive number'
        : ''
    },

    displayInvoiceBox(): boolean {
      return (
        this.displayCreateInvoiceMsgBox && this.currentAction === 'dollar-sign'
      )
    },

    requesterEmailAvailable(): boolean {
      return this.request.requester.email !== 'Email Not Available'
    },

    rows(): number {
      return this.requesterEmailAvailable ? 12 : 8
    },

    negativeInvoiceAmount(): boolean {
      return this.totalInvoiceAmount < 0
    },

    invalidInvoiceAmount(): boolean {
      return this.totalInvoiceAmount <= 0
    },

    disableSubmit(): boolean {
      return !(this.message.length > 0 && this.totalInvoiceAmount > 0)
    }
  },

  methods: {
    ...mapActions('request_show', [
      'setDisplayCreateInvoiceMsgBox',
      'processInvoice',
      'setCurrentAction',
      'recalculateAllCosts'
    ]),

    ...mapMutations('request_show', [
      'setTotalSelectedLineItems',
      'setTotalSelectedSurchargeItems',
      'setSubtotalInvoiceAmount',
      'setTotalInvoiceAmount',
      'setInvoiceItemToEdit',
      'setInvoiceLineItemType',
      'setInvoiceLineItemQuantity',
      'setInvoiceSelectedSurchargeItem',
      'setInvoiceSelectedLineItem',
      'setInvoiceAddItemModal',
      'setActiveModal'
    ]),

    truncate,

    formatCost(amount: number, maximumFractionDigits: number = 2) {
      return helpers.formatCost(amount, maximumFractionDigits)
    },

    openAddItemModal(): void {
      this.resetLineItemState()
      this.setInvoiceItemToEdit(null)
      this.setInvoiceAddItemModal(true)
    },

    resetLineItemState(): void {
      this.setInvoiceLineItemType('')
      this.setInvoiceLineItemQuantity(1)
      this.setInvoiceSelectedSurchargeItem('')
      this.setInvoiceSelectedLineItem('')
    },

    filterInvoiceTemplates(val: string): void {
      if (val === '') {
        this.templates = this.invoiceTemplates.map(
          (item: InvoiceTemplates) => ({
            value: item.id,
            label: item.invoice_template_name,
            description: item.invoice_template_description
          })
        )
        return
      }

      const filteredMsgs = this.invoiceTemplates.filter(
        // eslint-disable-next-line camelcase
        (msg: { invoice_template_name: string }) => {
          const regex = new RegExp(val, 'gi')
          return regex.test(msg.invoice_template_name)
        }
      )

      this.templates = filteredMsgs.map((item: InvoiceTemplates) => ({
        value: item.id,
        label: item.invoice_template_name,
        description: item.invoice_template_description
      }))
    },

    handleCancelInvoice(): void {
      this.message = ''
      this.selectedInvoiceTemplate = []
      this.setDisplayCreateInvoiceMsgBox(false)
    },

    setMessageTemplate(): void {
      this.message = this.selectedInvoiceTemplate
        .map((selected: InvoiceTemplateOption) => {
          return selected.description
        })
        .join(' ')
    },

    totalLineItems(): Array<ApiLineItemPayload> {
      return this.totalSelectedLineItems.map(
        (lineItem: SelectedLineItemOption) => {
          return {
            line_item_template_id: lineItem.value,
            name: lineItem.label,
            cost: Number(lineItem.cost),
            quantity: lineItem.total_count || 1,
            description: lineItem.description
          }
        }
      )
    },

    totalSurcharges(): Array<ApiSurchargeItemPayload> {
      return this.totalSelectedSurchargeItems.map(
        (surcharge: SurchargeOption) => {
          return {
            surcharge_template_id: surcharge.value,
            name: surcharge.label,
            amount: Number(surcharge.amount),
            cost_type: surcharge.cost_type,
            description: surcharge.description
          }
        }
      )
    },

    clearInvoiceLineItems(): void {
      this.setTotalSelectedSurchargeItems([])
      this.setTotalSelectedLineItems([])
      this.setSubtotalInvoiceAmount(0)
      this.setTotalInvoiceAmount(0)
    },

    handleSubmitInvoice(): void {
      const lineItems = this.totalLineItems()
      const surchargeItems = this.totalSurcharges()
      this.processInvoice({
        action: 'create',
        method: 'post',
        message: this.message,
        request_id: this.request.pretty_id,
        invoice_template_id: this.selectedInvoiceTemplate.id,
        total_amount: this.totalInvoiceAmount,
        line_items_attributes: lineItems,
        surcharges_attributes: surchargeItems
      }).then(() => {
        this.handleCancelInvoice()
        this.clearInvoiceLineItems()
      })
    }
  }
}
